import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { EventHubApiService, EventHubCatalogDetail } from './event-hub-api.service';

@Component({
  standalone: true,
  selector: 'ain-event-hub-catalog-detail',
  imports: [AinPageComponent, AinStateComponent, TableModule, ButtonModule, RouterLink],
  template: `
    <ain-page
      [title]="detail()?.eventType ?? t('eventHub.catalog.detail.title')"
      [description]="detail()?.description ?? t('eventHub.catalog.detail.description')">
      <div page-actions>
        <a [routerLink]="['/event-hub/catalog']">{{ t('eventHub.catalog.backToList') }}</a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (detail()) {
        <section class="header-card">
          <div><strong>{{ t('columns.version') }}:</strong> {{ detail()!.version }}</div>
          <div><strong>{{ t('eventHub.catalog.fields.ownerModule') }}:</strong> {{ detail()!.ownerModuleKey }}</div>
          <div><strong>{{ t('columns.status') }}:</strong> {{ statusLabel(detail()!.status) }}</div>
        </section>

        <section class="tabs">
          <button type="button" class="tab" [class.active]="activeTab() === 'schema'" (click)="activeTab.set('schema')">{{ t('eventHub.catalog.tabs.schema') }}</button>
          <button type="button" class="tab" [class.active]="activeTab() === 'example'" (click)="activeTab.set('example')">{{ t('eventHub.catalog.tabs.example') }}</button>
          <button type="button" class="tab" [class.active]="activeTab() === 'consumers'" (click)="activeTab.set('consumers')">{{ t('eventHub.catalog.tabs.consumers') }}</button>
          <button type="button" class="tab" [class.active]="activeTab() === 'versions'" (click)="activeTab.set('versions')">{{ t('eventHub.catalog.tabs.versions') }}</button>
        </section>

        @switch (activeTab()) {
          @case ('schema') {
            <pre class="code-block">{{ formatJson(detail()!.schemaJson) }}</pre>
          }
          @case ('example') {
            <pre class="code-block">{{ formatJson(detail()!.exampleJson) }}</pre>
          }
          @case ('consumers') {
            @if (!detail()!.consumers.length) {
              <ain-state type="empty" [title]="t('eventHub.catalog.consumers.empty')" />
            } @else {
              <p-table [value]="detail()!.consumers">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('eventHub.catalog.consumers.handlerKey') }}</th>
                    <th>{{ t('columns.version') }}</th>
                    <th>{{ t('columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.handlerKey }}</td>
                    <td>{{ row.eventVersion ?? t('common.all') }}</td>
                    <td>{{ row.isActive ? t('eventHub.catalog.consumers.active') : t('eventHub.catalog.consumers.inactive') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
          @case ('versions') {
            @if (!detail()!.otherVersions.length) {
              <ain-state type="empty" [title]="t('eventHub.catalog.versions.empty')" />
            } @else {
              <p-table [value]="detail()!.otherVersions">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('columns.version') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.version }}</td>
                    <td>{{ statusLabel(row.status) }}</td>
                    <td>
                      <a [routerLink]="['/event-hub/catalog', detail()!.eventType, 'versions', row.version]">{{ t('common.open') }}</a>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { display: grid; gap: 6px; margin-bottom: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 10px; background: #f9fafb; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .tab { border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tab.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 600; }
    .code-block { border: 1px solid #e5e7eb; border-radius: 10px; background: #0f172a; color: #e2e8f0; padding: 12px; overflow: auto; white-space: pre-wrap; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class EventHubCatalogDetailComponent implements OnInit {
  private readonly api = inject(EventHubApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);

  readonly detail = signal<EventHubCatalogDetail | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly activeTab = signal<'schema' | 'example' | 'consumers' | 'versions'>('schema');

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const eventType = params.get('eventType');
      const version = Number(params.get('version'));
      if (!eventType || !Number.isFinite(version)) {
        this.error.set(this.t('eventHub.catalog.detail.invalidRoute'));
        this.loading.set(false);
        return;
      }
      this.load(eventType, version);
    });
  }

  load(eventType: string, version: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getCatalogEntry(eventType, version).subscribe({
      next: item => {
        this.detail.set(item);
        this.loading.set(false);
      },
      error: () => {
        this.detail.set(null);
        this.error.set(this.t('eventHub.catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  formatJson(raw: string): string {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  statusLabel(status: EventHubCatalogDetail['status'] | number): string {
    const normalized = typeof status === 'number'
      ? ['Active', 'Deprecated', 'Retired'][status] ?? String(status)
      : String(status);
    return this.t(`eventHub.catalog.status.${normalized.toLowerCase()}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
